import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { LanguageService, LanguageCode } from '../../../services/i18n/language';

export type RoleKey = 'cargoOwner' | 'freightForwarder' | 'customsBroker' | 'supplier';

interface RoleContent {
  tag: string;
  title: string;
  lead: string;
  text: string;
  benefits: { title: string; text: string }[];
  steps: { title: string; text: string }[];
  pricing?: { title: string; price: string; unit?: string; text: string; highlight?: boolean }[];
  pricingNote?: string;
  faq: { q: string; a: string }[];
  ctaTitle: string;
  ctaText: string;
}

interface RolesFile {
  common: Record<string, string>;
  cargoOwner: RoleContent;
  freightForwarder: RoleContent;
  customsBroker: RoleContent;
  supplier: RoleContent;
}

/** Куда ведёт регистрация; брокеры подключаются через WhatsApp, своей формы у них нет. */
const REGISTRATION_ROUTE: Partial<Record<RoleKey, string>> = {
  cargoOwner: '/registration/cargo-owner',
  freightForwarder: '/registration/freight-forwarder',
  supplier: '/registration/supplier'
};

const WHATSAPP_MESSAGE_KEY = 'info.customsBroker.whatsappMessage';
const WHATSAPP_MESSAGE_FALLBACK = 'Здравствуйте! Хочу подключиться к Bilload как таможенный брокер.';

/**
 * Страница роли (/info/*): вступление, что получает пользователь, шаги, стоимость (только у грузовладельца),
 * частые вопросы и призыв. Тексты — в assets/roles/<язык>.json, роль задаётся в data маршрута.
 */
@Component({
  selector: 'app-role-info',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './role-info.component.html',
  styleUrl: './role-info.component.scss'
})
export class RoleInfoComponent implements OnInit, OnDestroy {
  role: RoleKey = 'cargoOwner';
  common: Record<string, string> = {};
  content: RoleContent | null = null;
  registrationRoute?: string;

  private readonly cache = new Map<LanguageCode, RolesFile>();
  private subscriptions = new Subscription();
  private loadSub?: Subscription;

  constructor(
    private http: HttpClient,
    private route: ActivatedRoute,
    private router: Router,
    private languageService: LanguageService,
    private cdr: ChangeDetectorRef
  ) {}

  get whatsappUrl(): string {
    const value = this.languageService.translate(WHATSAPP_MESSAGE_KEY);
    const message = value && value !== WHATSAPP_MESSAGE_KEY ? value : WHATSAPP_MESSAGE_FALLBACK;
    return `https://wa.me/77078880054?text=${encodeURIComponent(message)}`;
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.route.data.subscribe(data => {
        this.role = data['role'] ?? 'cargoOwner';
        this.registrationRoute = REGISTRATION_ROUTE[this.role];
        this.apply();
      })
    );
    this.subscriptions.add(
      this.languageService.getCurrentLanguage().subscribe(lang => this.load(lang))
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.loadSub?.unsubscribe();
  }

  /** Тарифы — на лендинге; переход с прокруткой к блоку «Стоимость и услуги». */
  goToTariffs(event: Event): void {
    event.preventDefault();
    this.router.navigateByUrl('/').then(() =>
      setTimeout(() => document.getElementById('tar')?.scrollIntoView({ behavior: 'smooth' }))
    );
  }

  private load(lang: LanguageCode): void {
    const cached = this.cache.get(lang);
    if (cached) {
      this.apply(cached);
      return;
    }
    this.loadSub?.unsubscribe();
    this.loadSub = this.http.get<RolesFile>(`/assets/roles/${lang}.json`).subscribe({
      next: file => {
        this.cache.set(lang, file);
        this.apply(file);
      },
      // Нет файла на этом языке — показываем русский
      error: () => lang !== 'ru' && this.load('ru')
    });
  }

  private apply(file?: RolesFile): void {
    const source = file ?? this.cache.get(this.languageService.getCurrentLanguageValue()) ?? this.cache.get('ru');
    if (!source) {
      return;
    }
    this.common = source.common;
    this.content = source[this.role];
    this.cdr.markForCheck();
  }
}
